import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { saveOnboardingPage } from "@/api/onboarding";
import OnboardingPreview from "./OnboardingPreview";
import MediaPicker from "@/components/ui/media-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { OnboardingPage } from "@/types/onboarding";

const LOCALES = [
  { code: "en", label: "English" },
  { code: "mz", label: "Mizo" },
] as const;

const localizedString = z.object({
  en: z.string().min(1, "English text is required"),
  mz: z.string().optional().default(""),
});

const optionalLocalizedString = z.object({
  en: z.string().optional().default(""),
  mz: z.string().optional().default(""),
});

const pageSchema = z.object({
  title: localizedString,
  content: optionalLocalizedString,
  mediaType: z.enum(["image", "lottie", "video"]).nullable().optional(),
  mediaUrl: z.string().optional(),
  mediaBgColor: z.string().optional(),
  tag: optionalLocalizedString,
  tagStyle: z.enum(["default", "success", "warning", "info"]).nullable().optional(),
  buttonText: localizedString,
  isActive: z.boolean(),
});

type PageFormValues = z.infer<typeof pageSchema>;

interface OnboardingPageFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: number;
  page?: OnboardingPage | null;
  position: number;
  totalPages: number;
  isSkippable?: boolean;
}

function toFormLocalized(value: Record<string, string> | string | null | undefined): { en: string; mz: string } {
  if (!value) return { en: "", mz: "" };
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return { en: parsed.en || "", mz: parsed.mz || "" };
      }
    } catch {
      // plain string, treat as English
    }
    return { en: value, mz: "" };
  }
  return { en: value.en || "", mz: value.mz || "" };
}

function toDbLocalized(value: { en?: string; mz?: string }): Record<string, string> {
  const result: Record<string, string> = {};
  if (value.en) result.en = value.en;
  if (value.mz) result.mz = value.mz;
  return result;
}

export default function OnboardingPageForm({
  open,
  onOpenChange,
  flowId,
  page,
  position,
  totalPages,
  isSkippable = true,
}: OnboardingPageFormProps) {
  const isEditMode = !!page;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [previewLocale, setPreviewLocale] = useState("en");

  const form = useForm<PageFormValues>({
    resolver: zodResolver(pageSchema),
    defaultValues: {
      title: { en: "", mz: "" },
      content: { en: "", mz: "" },
      mediaType: null,
      mediaUrl: "",
      mediaBgColor: "",
      tag: { en: "", mz: "" },
      tagStyle: null,
      buttonText: { en: "Next", mz: "" },
      isActive: true,
    },
  });

  useEffect(() => {
    if (page) {
      form.reset({
        title: toFormLocalized(page.title),
        content: toFormLocalized(page.content),
        mediaType: page.mediaType,
        mediaUrl: page.mediaUrl || "",
        mediaBgColor: page.mediaBgColor || "",
        tag: toFormLocalized(page.tag),
        tagStyle: page.tagStyle,
        buttonText: toFormLocalized(page.buttonText),
        isActive: page.isActive,
      });
    } else {
      form.reset({
        title: { en: "", mz: "" },
        content: { en: "", mz: "" },
        mediaType: null,
        mediaUrl: "",
        mediaBgColor: "",
        tag: { en: "", mz: "" },
        tagStyle: null,
        buttonText: { en: "Next", mz: "" },
        isActive: true,
      });
    }
  }, [page, form]);

  const mutation = useMutation({
    mutationFn: (values: PageFormValues) =>
      saveOnboardingPage(
        {
          flowId,
          position: page?.position ?? position,
          mediaType: values.mediaType || null,
          mediaUrl: values.mediaUrl || null,
          mediaBgColor: values.mediaBgColor || null,
          tag: values.tag?.en ? toDbLocalized(values.tag) : null,
          tagStyle: values.tagStyle || null,
          title: toDbLocalized(values.title),
          content: values.content?.en ? toDbLocalized(values.content) : null,
          buttonText: toDbLocalized(values.buttonText),
          isActive: values.isActive,
        },
        page?.id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flow"] });
      toast({
        title: isEditMode ? "Page updated" : "Page created",
        description: isEditMode
          ? "The onboarding page has been updated."
          : "The onboarding page has been created.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: PageFormValues) => {
    mutation.mutate(values);
  };

  const watched = form.watch();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Page" : "Add Page"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Form fields */}
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            id="page-form"
          >
            {/* Localized text fields */}
            <Tabs defaultValue="en" className="w-full">
              <TabsList className="w-full">
                {LOCALES.map((l) => (
                  <TabsTrigger key={l.code} value={l.code} className="flex-1">
                    {l.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {LOCALES.map((l) => (
                <TabsContent key={l.code} value={l.code} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor={`title-${l.code}`}>
                      Title {l.code === "en" ? "*" : ""}
                    </Label>
                    <Input
                      id={`title-${l.code}`}
                      placeholder={l.code === "mz" ? "Mizo translation" : ""}
                      {...form.register(`title.${l.code}` as const)}
                    />
                    {l.code === "en" && form.formState.errors.title?.en && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.title.en.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`content-${l.code}`}>Content</Label>
                    <Textarea
                      id={`content-${l.code}`}
                      rows={3}
                      placeholder={l.code === "mz" ? "Mizo translation" : ""}
                      {...form.register(`content.${l.code}` as const)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`tag-${l.code}`}>Tag</Label>
                    <Input
                      id={`tag-${l.code}`}
                      placeholder={l.code === "mz" ? "e.g. THAR" : "e.g. NEW"}
                      {...form.register(`tag.${l.code}` as const)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`buttonText-${l.code}`}>
                      Button Text {l.code === "en" ? "*" : ""}
                    </Label>
                    <Input
                      id={`buttonText-${l.code}`}
                      placeholder={l.code === "mz" ? "Mizo translation" : ""}
                      {...form.register(`buttonText.${l.code}` as const)}
                    />
                    {l.code === "en" && form.formState.errors.buttonText?.en && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.buttonText.en.message}
                      </p>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* Non-localized fields */}
            <div className="space-y-2">
              <Label>Media Type</Label>
              <Select
                value={form.watch("mediaType") || "none"}
                onValueChange={(val) =>
                  form.setValue(
                    "mediaType",
                    val === "none" ? null : (val as "image" | "lottie" | "video")
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select media type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="lottie">Lottie Animation</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.watch("mediaType") === "image" && (
              <div className="space-y-2">
                <Label>Media Image</Label>
                <MediaPicker
                  onSelect={(url) => form.setValue("mediaUrl", url)}
                  currentValue={form.watch("mediaUrl")}
                  title="Select Page Media"
                  description="Choose or upload an image for this onboarding page"
                  buttonText="Select Image"
                  showPreview={true}
                />
              </div>
            )}

            {(form.watch("mediaType") === "lottie" ||
              form.watch("mediaType") === "video") && (
              <div className="space-y-2">
                <Label htmlFor="mediaUrl">
                  {form.watch("mediaType") === "lottie"
                    ? "Lottie JSON URL"
                    : "Video URL"}
                </Label>
                <Input
                  id="mediaUrl"
                  placeholder={
                    form.watch("mediaType") === "lottie"
                      ? "https://example.com/animation.json"
                      : "https://example.com/video.mp4"
                  }
                  {...form.register("mediaUrl")}
                />
              </div>
            )}

            {form.watch("mediaType") && (
              <div className="space-y-2">
                <Label htmlFor="mediaBgColor">Media Background Color</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-9 w-9 rounded border flex items-center justify-center overflow-hidden">
                    <input
                      type="color"
                      id="mediaBgColor"
                      value={form.watch("mediaBgColor") || "#f3f4f6"}
                      onChange={(e) => form.setValue("mediaBgColor", e.target.value)}
                      className="w-12 h-12 border-0 p-0 m-0 cursor-pointer"
                      style={{ transform: "scale(1.5)" }}
                    />
                  </div>
                  <Input
                    value={form.watch("mediaBgColor") || ""}
                    onChange={(e) => form.setValue("mediaBgColor", e.target.value)}
                    placeholder="#f3f4f6"
                    className="font-mono w-28"
                    maxLength={7}
                  />
                  <div className="flex flex-wrap gap-1">
                    {[
                      "#F8F9FA",
                      "#E9ECEF",
                      "#F8F0E3",
                      "#FFF3BF",
                      "#D8F3DC",
                      "#D7E3FC",
                      "#EFD9CE",
                      "#212529",
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="w-6 h-6 rounded-full border border-border hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-primary"
                        style={{ backgroundColor: color }}
                        onClick={() => form.setValue("mediaBgColor", color)}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Tag Style</Label>
              <Select
                value={form.watch("tagStyle") || "default"}
                onValueChange={(val) =>
                  form.setValue(
                    "tagStyle",
                    val as "default" | "success" | "warning" | "info"
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="isActive"
                checked={form.watch("isActive")}
                onCheckedChange={(val) => form.setValue("isActive", val)}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending
                  ? "Saving..."
                  : isEditMode
                  ? "Update Page"
                  : "Add Page"}
              </Button>
            </div>
          </form>

          {/* Live preview */}
          <div className="flex flex-col items-center gap-3 pt-4">
            <div className="flex gap-1 rounded-lg border p-1 dark:border-gray-700">
              {LOCALES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    previewLocale === l.code
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setPreviewLocale(l.code)}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <OnboardingPreview
              page={{
                title: watched.title,
                content: watched.content?.en ? watched.content : null,
                mediaType: watched.mediaType || null,
                mediaUrl: watched.mediaUrl || null,
                mediaBgColor: watched.mediaBgColor || null,
                tag: watched.tag?.en ? watched.tag : null,
                tagStyle: watched.tagStyle || null,
                buttonText: watched.buttonText,
                isActive: watched.isActive,
              }}
              totalPages={totalPages}
              currentIndex={page ? page.position : position}
              isSkippable={isSkippable}
              locale={previewLocale}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
